import React from 'react';
import { T, FONT_STACK } from '../customer/customers.theme';

/**
 * What the pad is currently doing, and the way out of it.
 *
 * Two jobs, one control, because they are the same action underneath —
 * `clearCart` empties the cart AND drops `editingOrderId`:
 *
 *   building a new order   "3 items"            → Clear
 *   amending a saved one   "Editing order #214" → Cancel edit
 *
 * Before this there was no way back from either. A waiter who tapped Edit on
 * the wrong card was stuck amending it: the only exits were placing the order
 * or signing out, and the pad gave no sign that Place Order would now update
 * somebody else's table rather than open a new one.
 *
 * Hidden entirely when there is nothing to undo, so a fresh pad is not carrying
 * a disabled control.
 */

const COPY = {
  editing: (n) => (n ? `Editing order #${n}` : 'Editing an order'),
  cancelEdit: 'Cancel edit',
  clear: 'Clear',
  confirmClear: 'Sure?',
  items: (n) => (n === 1 ? '1 item' : `${n} items`),
};

/** How long the Clear button stays armed before it forgets it was pressed. */
const CONFIRM_MS = 3000;

export default function CartStatusBar({ itemCount, editingOrderNumber, editing, onClear }) {
  const [armed, setArmed] = React.useState(false);

  // Disarms itself, so a half-pressed Clear left on screen during service
  // cannot be completed by a later, unrelated tap.
  React.useEffect(() => {
    if (!armed) return undefined;
    const timer = setTimeout(() => setArmed(false), CONFIRM_MS);
    return () => clearTimeout(timer);
  }, [armed]);

  // An edit that ends, or a cart that empties, leaves nothing to confirm.
  React.useEffect(() => {
    if (!editing && itemCount === 0) setArmed(false);
  }, [editing, itemCount]);

  if (!editing && itemCount === 0) return null;

  const press = () => {
    // Cancelling an edit destroys nothing — the saved order is untouched on the
    // server — so it goes on one tap. Emptying a cart someone just built does
    // not.
    if (editing || armed) {
      setArmed(false);
      onClear();
      return;
    }
    setArmed(true);
  };

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '9px 12px',
        background: editing ? T.selectedBg : T.sunk,
        border: `1px solid ${editing ? T.selectedBorder : T.border}`,
        borderRadius: T.radiusPanel,
        fontFamily: FONT_STACK,
        boxSizing: 'border-box',
      }}
    >
      {editing ? (
        <span
          style={{
            width: 7,
            height: 7,
            flexShrink: 0,
            background: T.brand,
            borderRadius: T.radiusPill,
          }}
        />
      ) : null}

      <span
        style={{
          flex: 1,
          minWidth: 0,
          fontSize: 12.5,
          fontWeight: editing ? 700 : 500,
          color: editing ? T.brandInk : T.inkSoft,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {editing ? COPY.editing(editingOrderNumber) : COPY.items(itemCount)}
      </span>

      <button
        type="button"
        onClick={press}
        style={{
          height: 30,
          padding: '0 14px',
          flexShrink: 0,
          background: armed ? T.danger : T.surface,
          border: `1px solid ${armed ? T.danger : T.borderStrong}`,
          borderRadius: T.radiusPill,
          font: 'inherit',
          fontFamily: FONT_STACK,
          fontSize: 12.5,
          fontWeight: 600,
          color: armed ? T.surface : editing ? T.brandInk : T.inkSoft,
          cursor: 'pointer',
        }}
      >
        {editing ? COPY.cancelEdit : armed ? COPY.confirmClear : COPY.clear}
      </button>
    </div>
  );
}
